/**
 * SessionExpiredModal — global session-expiry surfacing.
 *
 * RC 1.44.H2 (2026-04-30) — P1 Session-resilience.
 * Replaces the confusing "Database query failed" banner users saw
 * after ~1 h of activity (Supabase JWT expiry). The flow is now:
 *
 *   1. `apiFetch` hits a 401 from the backend.
 *   2. `apiFetch` transparently asks Supabase to refresh the session
 *      and retries the request once.
 *   3. If the retry also fails (refresh token exhausted, user
 *      revoked access, device offline), `apiFetch` fires a
 *      `ndorflow:session-expired` DOM event on window.
 *   4. This component listens for the event and opens an honest,
 *      single-CTA modal: "Your session has expired — Sign in again".
 *   5. "Sign in again" clears the Supabase session and navigates to
 *      /login. No further work can sneak in behind the modal.
 *
 * The modal is mounted at the shell level (`AppLayout.js`) so it is
 * always in the tree regardless of which page the user is on.
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Clock } from 'lucide-react';
import { supabase } from '@/lib/supabase';

export default function SessionExpiredModal() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('ndorflow:session-expired', handler);
    return () => window.removeEventListener('ndorflow:session-expired', handler);
  }, []);

  const handleSignIn = useCallback(async () => {
    // Clear Supabase's own cached session so the login screen starts
    // from a clean slate. Wrapped in try/catch because a non-session
    // signOut can throw on some Supabase builds.
    try { await supabase.auth.signOut(); } catch { /* ignore */ }
    setOpen(false);
    navigate('/login', { replace: true });
  }, [navigate]);

  return (
    <Dialog
      open={open}
      // Prevent the user from dismissing the modal by clicking outside
      // — the only safe action is to re-authenticate.
      onOpenChange={(v) => { if (!v) handleSignIn(); else setOpen(v); }}
    >
      <DialogContent
        data-testid="session-expired-modal"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-500/10 text-amber-600">
              <Clock className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <DialogTitle>Your session has expired</DialogTitle>
              <DialogDescription className="mt-1">
                For your security, NDORFlow sessions time out after a
                period of inactivity. Please sign in again to continue.
                Your work is saved — nothing has been lost.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <DialogFooter>
          <Button
            onClick={handleSignIn}
            data-testid="session-expired-sign-in-btn"
            className="w-full sm:w-auto"
          >
            Sign in again
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
